import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { PencilFill, X, ChevronDown } from 'react-bootstrap-icons';
import Dropzone from 'react-dropzone';

// --- IMPORTS ---
import { uploadFileAndGetUrl } from '../../utils/uploadFile';
import { useFileUpload } from '../../hooks/useFileUpload';
import { useUser } from '../../contextManager/user.context';
import useAuth, { ProfilePatch } from '../../hooks/auth.service';
import { styled } from '../../styles/stitches.config';

// Import the Layout Wrappers
import {
  AccordionItemWrapper,
  CardHeader,
  CardContent,
  CardFooter,
  HeaderIconButton,
  ChevronToggle 
} from '../../styles/profile/layout.style';

import { InfoRow, InfoLabel, InfoValue, FileLink } from '../../styles/profile/form.style';
import { ActionButton } from '../../styles/profile/profile.style';
import { DropzoneContainer } from '../../styles/upload-dialog.style';

// --- LOCAL STYLED COMPONENTS ---
export const ManualField = styled('div', {
  position: 'relative', display: 'flex', flexDirection: 'column',
  gap: '0.5rem', alignItems: 'flex-start', width: '100%', marginBottom: '1rem',
});
export const ManualLabel = styled('label', {
  textAlign: 'left', 
  whiteSpace: 'normal',
  maxWidth: '100%',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  display: 'block',
  fontSize: '$sm', 
  fontWeight: 500, 
  color: '$slate11',
});
export const ManualInput = styled('input', {
  all: 'unset', boxSizing: 'border-box', fontSize: '$sm', width: '100%',
  border: '1px solid $elementBorder', borderRadius: '0.5rem', padding: '0.75rem',
  height: '2.75rem', backgroundColor: 'white',
  '&:focus': { borderColor: '$blue8' },
  '&:disabled': { backgroundColor: '$slate2', color: '$slate9', cursor: 'not-allowed' },
});

// --- PROPS INTERFACE ---
interface ProfileDetailsProps {
  isOpen: boolean;
  onToggle: () => void;
  isMobile?: boolean;
  onVerificationThreat?: (field: string, isThreatened: boolean) => void;
}

// --- HELPER ---
const getFileName = (url: string): string => {
  if (!url) return '';
  try {
    const decoded = decodeURIComponent(url);
    const parts = decoded.split('/');
    return parts[parts.length - 1].split('?')[0] || '';
  } catch { return url; }
};

// --- MAIN COMPONENT ---
export const ProfileDetails: React.FC<ProfileDetailsProps> = ({ isOpen, onToggle, isMobile = false, onVerificationThreat}) => {
  
  // 1. State Hooks
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { userDetails } = useUser();
  const { patchProfile } = useAuth();
  const { uploadFile } = useFileUpload();

  // 2. Form State
  const [formData, setFormData] = useState<{ CVFile?: File; proofFile?: File; }>({});
  const [isCVMarkedForDeletion, setIsCVMarkedForDeletion] = useState(false);
  const [isProofMarkedForDeletion, setIsProofMarkedForDeletion] = useState(false);

  // 3. Derived State (Memos)
  const originalCVName = useMemo(() => getFileName(userDetails?.CVFileUrl || ''), [userDetails?.CVFileUrl]);
  const originalProofName = useMemo(() => getFileName(userDetails?.proofFileUrl || ''), [userDetails?.proofFileUrl]);

  const cvDisplayName = useMemo(() => userDetails?.CVFileUrl ? `${userDetails.name} - CV` : '', [userDetails?.CVFileUrl, userDetails?.name]);
  const proofDisplayName = useMemo(() => userDetails?.proofFileUrl ? `${userDetails.name} - Proof of Degree` : '', [userDetails?.proofFileUrl, userDetails?.name]);
  
  const isDirty = !!formData.CVFile || !!formData.proofFile || isCVMarkedForDeletion || isProofMarkedForDeletion;

  // ── Verification threat signalling ────────────────────────────
  const isVerified = !!userDetails?.account_verified;

  useEffect(() => {
    if (!onVerificationThreat || !isVerified) return;
    // Proof is threatened when marked for deletion AND no replacement file staged
    const proofThreatened = isProofMarkedForDeletion && !formData.proofFile;
    onVerificationThreat('Proof of degree', proofThreatened);
  }, [isProofMarkedForDeletion, formData.proofFile, isVerified, onVerificationThreat]);

  // Clear threats when leaving edit mode
  useEffect(() => {
    if (!isEditing && onVerificationThreat) {
      onVerificationThreat('Proof of degree', false);
    }
  }, [isEditing, onVerificationThreat]);

  // 4. Handlers
  const handleEditClick = () => { setFormData({}); setIsCVMarkedForDeletion(false); setIsProofMarkedForDeletion(false); setIsEditing(true); };
  
  const handleCVDrop = useCallback((files: File[]) => { if (files[0]) { setFormData(prev => ({ ...prev, CVFile: files[0] })); setIsCVMarkedForDeletion(false); } }, []);
  const handleProofDrop = useCallback((files: File[]) => { if (files[0]) { setFormData(prev => ({ ...prev, proofFile: files[0] })); setIsProofMarkedForDeletion(false); } }, []);
  
  const handleRemoveCV = (e: React.MouseEvent) => { e.stopPropagation(); if (formData.CVFile) setFormData(prev => ({ ...prev, CVFile: undefined })); else if (originalCVName) setIsCVMarkedForDeletion(true); };
  const handleRemoveProof = (e: React.MouseEvent) => { e.stopPropagation(); if (formData.proofFile) setFormData(prev => ({ ...prev, proofFile: undefined })); else if (originalProofName) setIsProofMarkedForDeletion(true); };

  const handleSave = async () => {
    if (!isDirty) return;
    setIsSaving(true);
    const patchData: Partial<ProfilePatch> = {};
    try {
      if (formData.CVFile) patchData.CVFileUrl = await uploadFile(formData.CVFile) || '';
      else if (isCVMarkedForDeletion) patchData.CVFileUrl = '';

      if (formData.proofFile) patchData.proofFileUrl = await uploadFile(formData.proofFile) || '';
      else if (isProofMarkedForDeletion) patchData.proofFileUrl = '';

      if (Object.keys(patchData).length > 0) {
        await patchProfile(
          patchData,
          { loading: 'Saving documents...', success: 'Documents saved.' },
        );
      }
      setIsEditing(false);
    } catch (error) { console.error("Failed to save:", error); } 
    finally { setIsSaving(false); }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => { e.preventDefault(); handleSave(); };
  const handleCancel = () => { setIsEditing(false); };

  // --- 5. RENDER ---
  return (
    <AccordionItemWrapper 
      isOpen={isOpen}
      fitContent={isOpen && !isEditing}
      >

      <CardHeader 
        onClick={onToggle} 
        collapsed={!isOpen} 
        css={{ cursor: 'pointer' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
           {!isMobile && 
           <ChevronToggle collapsed={!isOpen}>
              <ChevronDown />
           </ChevronToggle>
          }
           <span>Personal Details</span>
        </div>
      
        <div onClick={(e) => e.stopPropagation()}>
          {isOpen && !isEditing && (
             <HeaderIconButton onClick={handleEditClick}>
               <PencilFill size={18} />
             </HeaderIconButton>
          )}
        </div>
      </CardHeader>

      {isOpen && (
          isEditing ? (
            <form
              onSubmit={handleSubmit} 
              style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                flex: '1 1 0%',
                minHeight: 0,
                overflow: 'hidden',
            }}>
            <CardContent>   
                <ManualField>
                  <ManualLabel>Full Name</ManualLabel>
                  <ManualInput value={userDetails?.name || ''} disabled />
                </ManualField>
                <ManualField>
                  <ManualLabel>Email Address</ManualLabel>
                  <ManualInput value={userDetails?.email || ''} disabled />
                </ManualField>

                <ManualField>
                  <ManualLabel>Upload CV (PDF)</ManualLabel>
                  <Dropzone onDrop={handleCVDrop} accept={{ 'application/pdf': [] }} maxFiles={1}>
                    {({ getRootProps, getInputProps, isDragActive }) => (
                      <DropzoneContainer {...getRootProps()} state={formData.CVFile || (!isCVMarkedForDeletion && originalCVName) ? 'success' : 'idle'} css={{ background: isDragActive ? '$elementBackgroundHover' : '$elementBackground' }}>
                        <input {...getInputProps()} />
                        {formData.CVFile?.name || (!isCVMarkedForDeletion && originalCVName) ? (
                          <div style={{display:'flex', alignItems:'center', gap:'0.5rem'}}>
                            <span>{formData.CVFile?.name || cvDisplayName}</span>
                            <X size={22} onClick={handleRemoveCV} style={{cursor:'pointer'}}/>
                          </div>
                        ) : <p>Drag PDF here</p>}
                      </DropzoneContainer>
                    )}
                  </Dropzone>
                </ManualField>

                <ManualField>
                  <ManualLabel>Proof of Degree</ManualLabel>
                  <Dropzone onDrop={handleProofDrop} accept={{ 'application/pdf': [], 'image/*': []}} maxFiles={1}>
                    {({ getRootProps, getInputProps, isDragActive }) => (
                      <DropzoneContainer {...getRootProps()} state={formData.proofFile || (!isProofMarkedForDeletion && originalProofName) ? 'success' : 'idle'} css={{ background: isDragActive ? '$elementBackgroundHover' : '$elementBackground' }}>
                        <input {...getInputProps()} />
                        {formData.proofFile?.name || (!isProofMarkedForDeletion && originalProofName) ? (
                          <div style={{display:'flex', alignItems:'center', gap:'0.5rem'}}>
                            <span>{formData.proofFile?.name || proofDisplayName}</span>
                            <X size={22} onClick={handleRemoveProof} style={{cursor:'pointer'}}/>
                          </div>
                        ) : <p>Drag file here</p>}
                      </DropzoneContainer>
                    )}
                  </Dropzone>
                </ManualField>
              </CardContent>

                <CardFooter>
                    <ActionButton type="button" intent="secondary" onClick={handleCancel}>Cancel</ActionButton>
                    <ActionButton type="submit" intent="primary" disabled={!isDirty}>
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </ActionButton>
                </CardFooter>
            </form>
          ) : (
            <>
            <CardContent>
              <InfoRow>
                <InfoLabel>Full Name</InfoLabel>
                <InfoValue>{userDetails?.name || 'Not Provided'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Email Address</InfoLabel>
                <InfoValue>{userDetails?.email || 'Not Provided'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>CV</InfoLabel>
                <InfoValue>
                  {userDetails?.CVFileUrl ? (
                    <FileLink href={userDetails.CVFileUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: 'blue' }}>
                      {cvDisplayName}
                    </FileLink>
                  ) : 'Not Provided'}
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Proof of Degree</InfoLabel>
                <InfoValue>
                  {userDetails?.proofFileUrl ? (
                    <FileLink href={userDetails.proofFileUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: 'blue' }}>
                      {proofDisplayName}
                    </FileLink>
                  ) : 'Not Provided'}
                </InfoValue>
              </InfoRow>
              </CardContent>
            </>
          )
      )}
    </AccordionItemWrapper>
  );
};